<template>
  <el-dialog class="transaction-detail-dialog" :width="1300" :model-value="dialogVisible" :append-to-body="true" :show-close="true" center align-center @closed="closeDialog">
    <div class="flex items-center justify-between px-30 pb-20 pt-40">
      <div class="text-22 font-semibold text-black">{{ detail?.name }}</div>
      <div class="flex items-center">
        <span class="mr-15 text-14 leading-none text-black">{{ $t('日期选择') }}</span>
        <el-date-picker v-model="echartData.curDate" type="date" :placeholder="$t('请选择日期')" size="large" @change="changeDate" />
      </div>
    </div>

    <div class="detail-statistics mb-10 flex items-center justify-between">
      <span class="text-14 text-gray-light">{{ $t('成交数量(件)') }}</span>
      <div>
        <div v-if="transactionQuery.platform !== 0" class="flex items-center justify-end">
          <span>Steam :</span>

          <span>{{ $t('总成交') }}</span>
          <span class="text-blue">{{ overviewData.steam?.transactionCount }}</span>
          <span>{{ $t('件') }},</span>
          <span>{{ $t('总成交额') }}</span>
          <span class="text-[#FD802C]">{{ monetaryUnit() }}{{ overviewData.steam?.transactionAmount }}</span>
          <span>,</span>
          <span>成交均价</span>
          <span class="text-[#FD802C]">{{ monetaryUnit() }}{{ overviewData.steam?.avgPrice }}</span>
        </div>
        <div v-if="transactionQuery.platform !== 1" class="flex items-center justify-end">
          <span>{{ $t('三方平台') }}:</span>
          <span class="text-blue">{{ overviewData.third?.sellerCount }}</span>
          <span>{{ $t('位卖家') }},</span>
          <span>{{ $t('总成交') }}</span>
          <span class="text-blue">{{ overviewData.third?.transactionCount }}</span>
          <span>{{ $t('件') }},</span>
          <span>{{ $t('总成交额') }}</span>
          <span class="text-[#FD802C]">{{ monetaryUnit() }}{{ overviewData.third?.transactionAmount }}</span>
          <span>,</span>
          <span>成交均价</span>
          <span class="text-[#FD802C]">{{ monetaryUnit() }}{{ overviewData.third?.avgPrice }}</span>
        </div>
      </div>
    </div>

    <div class="detail-chart">
      <template v-if="chartLoading">
        <CommonLoading />
      </template>
      <template v-else>
        <div id="J_EchartArea"></div>
      </template>
    </div>

    <transication
      ref="transicationref"
      :transication-query="transactionQuery"
      :special-styles="specialStyles"
      :market-hash-name="props.detail.marketHashName"
      :end-time-query="endTimeQuery"
      :start-time-query="startTimeQuery"
      @change-platform="changePlatform"
    ></transication>
  </el-dialog>
</template>

<script setup>
import * as echarts from 'echarts/core'
import { GridComponent } from 'echarts/components'
import { BarChart } from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import { formatSortVal } from '~/utils/index'
import dayjs from 'dayjs'
import counter from '~/pages/indexcomponents/counter.vue'
import sortarrow from '~/components/common/sortarrow.vue'
import wearbar from '~/components/common/wearbar.vue'
import empty from '~/components/common/empty.vue'
import transication from './transication.vue'

import { getOneDayDataQueryEcharts, getDealInfo, getOverview } from '~~/api/steam.js'

const { t } = useI18n()
const $t = t

const emit = defineEmits(['close'])
const props = defineProps({
  dialogVisible: {
    type: Boolean,
    default: false
  },
  detail: {
    type: Object,
    default: () => {}
  },
  transactionData: {
    type: Object,
    default: () => {}
  }
})
const endTimeQuery = ref('')
const startTimeQuery = ref('')
const chartLoading = ref(false)
const listLoading = ref(false)
const sortData = ref({
  tradeTime: 'DESC'
})

const transicationref = ref(null)
const echartData = reactive({
  curDate: props.transactionData.curDate || null,
  curTime: `${props.transactionData.curTime}` || null,
  selectIndex: null,
  xlist: [],
  ylist: [],
  total: 0
})
const list = ref([])

const transactionQuery = ref({
  itemId: props.detail.itemId,
  pageSize: 20,
  nextId: '',
  sortBy: 'tradeTime',
  sortType: 'desc'
})

const specialStyles = ref([])

const overviewData = ref({})

onMounted(() => {
  let startTime = new Date(`${echartData.curDate} 00:00:00`).getTime() / 1000
  let endTime = new Date(`${echartData.curDate} 23:59:59`).getTime() / 1000

  if (echartData.curTime) {
    startTime = new Date(`${echartData.curDate} ${echartData.curTime}:00:00`).getTime() / 1000
    endTime = new Date(`${echartData.curDate} ${echartData.curTime}:59:59`).getTime() / 1000
  }

  transactionQuery.value.startTime = startTime
  transactionQuery.value.endTime = endTime
  const { detail } = props
  getDealInfo({ itemId: detail.itemId }).then(res => {
    specialStyles.value = res.data.specialStyles
  })
  getData(startTime, endTime)
})

// 日期选择
const changeDate = val => {
  const startTime = new Date(val).getTime() / 1000
  const endTime = new Date(val).getTime() / 1000 + 23 * 3600 + 59 * 60 + 59

  echartData.curDate = dayjs(startTime * 1000).format('YYYY-MM-DD')
  startTimeQuery.value = startTime
  endTimeQuery.value = endTime

  getData(startTime, endTime)
}

// 表格排序
const changeSort = curkey => {
  sortData.value[curkey] = formatSortVal(sortData.value[curkey], 'DESC')

  list.value = list.value.sort((a, b) => {
    if (sortData.value[curkey] === 'DESC') {
      // 降序
      return b[curkey] - a[curkey]
    } else {
      return a[curkey] - b[curkey]
    }
  })

  // 筛选数据处理
  for (let key in sortData.value) {
    if (curkey != key) {
      delete sortData.value[key]
    }
  }
}

// 获取数据
const getData = (startTime, endTime) => {
  getEchartsData(startTime)
  getDetailsData(startTime, endTime)
}

// 一天数据查询
const getEchartsData = startTime => {
  const { detail } = props

  chartLoading.value = true
  echartData.xlist = []
  echartData.ylist = []
  echartData.total = 0
  console.log('%c@@@startTime===>', 'color:green;font-size:15px', startTime)
  getOneDayDataQueryEcharts({
   
    itemId: detail.itemId,
    dt: dayjs(startTime * 1000).format('YYYY-MM-DD'),
    hasSteam: transactionQuery.value.platform,
    dt1:startTime
  })
    .then(res => {
      let oData = res.data
      const curHour = echartData.curTime ? new Date(`${echartData.curDate} ${echartData.curTime}:59:59`).getTime() / 1000 : null
      chartLoading.value = false

      oData = oData.sort((a, b) => {
        return a[0] - b[0]
      })

      oData.forEach((item, index) => {
        const yItem = {
          curTime: item[0],
          value: item[1]
        }

        echartData.xlist.push(dayjs(item[0] * 1000).format('HH'))

        if (item[0] == curHour) {
          yItem.itemStyle = {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(255, 0, 32, 1)'
              },
              {
                offset: 1,
                color: 'rgba(255, 0, 32, 0.06)'
              }
            ]),
            borderColor: '#FF0020'
          }
          yItem.label = {
            color: '#FF0020'
          }
          echartData.selectIndex = index
        }
        echartData.ylist.push(yItem)
      })

      echartData.total = oData.length

      setTimeout(() => {
        echartInit()
      })
    })
    .catch(() => {
      chartLoading.value = false
    })
}

// 区间详细数据查询
const getDetailsData = (startTime, endTime) => {
  const { detail } = props
  transactionQuery.startTime = startTime
  transactionQuery.endTime = endTime

  listLoading.value = true
  list.value = []
  getOverview({
    itemId: props.detail.itemId,
    dt: dayjs(startTime * 1000).format('YYYY-MM-DD'),
    startTime: startTime,
    endTime: endTime,
    hasSteam: transactionQuery.value.platform
  }).then(res => {
    overviewData.value = res.data
  })

  setTimeout(() => {
    listLoading.value = false
    if (transicationref.value) {
      transicationref.value.reset()
    }
  }, 500)
}

// 柱状图初始化
const echartInit = () => {
  echarts.use([GridComponent, BarChart, UniversalTransition, CanvasRenderer])

  const chartDom = document.getElementById('J_EchartArea')
  const myChart = echarts.init(chartDom)
  const option = {
    grid: {
      top: 20,
      right: '5%',
      bottom: 40,
      left: '5%'
    },
    textStyle: {
      fontSize: 14,
      color: '#979CAE'
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      axisLine: {
        lineStyle: {
          color: '#F0F1F5'
        }
      },
      axisTick: {
        lineStyle: {
          color: '#F0F1F5'
        }
      },
      axisLabel: {
        formatter: function (val) {
          const curHour = Number(val) //+ 1

          return curHour < 10 ? `0${curHour}` : curHour
        }
      },
      data: echartData.xlist
    },
    yAxis: {
      type: 'value',
      // min: 'dataMin',
      max: 'dataMax',
      axisLabel: {
        fontSize: 14
      },
      splitLine: {
        lineStyle: {
          color: '#F0F1F5'
        }
      }
    },
    series: [
      {
        type: 'bar',
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: 'rgba(12, 196, 232, 1)'
            },
            {
              offset: 1,
              color: 'rgba(232, 252, 255, 0.40)'
            }
          ]),
          emphasis: {
            opacity: 0.5
          },
          borderWidth: 1,
          borderColor: '#51E3FF'
        },
        label: {
          show: true,
          fontSize: 14,
          color: '#2BCCEC',
          position: 'top'
        },
        data: echartData.ylist
      }
    ]
  }

  option && myChart.setOption(option)

  myChart.on('click', function (param) {
    let startTime = null
    let endTime = null

    if (echartData.selectIndex !== null) {
      echartData.ylist[echartData.selectIndex].itemStyle = {}
      echartData.ylist[echartData.selectIndex].label = {}
    }

    if (echartData.selectIndex === param.dataIndex) {
      startTime = new Date(`${echartData.curDate} 00:00:00`).getTime() / 1000
      endTime = new Date(`${echartData.curDate} 23:59:59`).getTime() / 1000

      echartData.curTime = null
      echartData.selectIndex = null
    } else {
      startTime = Number(param.data.curTime) + 1 - 60 * 60
      endTime = Number(param.data.curTime) // + 60 * 60

      echartData.curTime = dayjs(param.data.curTime * 1000).format('HH')
      echartData.ylist[param.dataIndex].itemStyle = {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: 'rgba(255, 0, 32, 1)'
          },
          {
            offset: 1,
            color: 'rgba(255, 0, 32, 0.06)'
          }
        ]),
        borderColor: '#FF0020'
      }
      echartData.ylist[param.dataIndex].label = {
        color: '#FF0020'
      }

      echartData.selectIndex = param.dataIndex
    }

    option.series[0].data = echartData.ylist
    myChart.setOption(option)
    endTimeQuery.value = endTime
    startTimeQuery.value = startTime
    getDetailsData(startTime, endTime)
  })
}

// 隐藏
const closeDialog = () => {
  emit('close')
}

// 平台筛选发生变更重新获取列表数据
const changePlatform = platform => {
  transactionQuery.value.platform = platform
  let startTime = new Date(`${echartData.curDate} 00:00:00`).getTime() / 1000
  let endTime = new Date(`${echartData.curDate} 23:59:59`).getTime() / 1000

  if (echartData.curTime) {
    startTime = new Date(`${echartData.curDate} ${echartData.curTime}:00:00`).getTime() / 1000
    endTime = new Date(`${echartData.curDate} ${echartData.curTime}:59:59`).getTime() / 1000
  }
  getData(startTime, endTime)
}
</script>

<style lang="scss">
.transaction-detail-dialog {
  background: url('~/assets/images/common/common-dialog-header-bg.png') no-repeat $white-color;
  border-radius: 4px;

  .el-dialog__header {
    padding: 0 !important;

    .el-dialog__headerbtn {
      width: 16px;
      height: 16px;
      line-height: 1;
      font-size: 16px;
      top: 16px;
      right: 16px;

      .el-dialog__close {
        width: 16px;
        height: 16px;
        line-height: 16px;
        font-family: 'iconfont';
        font-style: normal;

        &:hover {
          animation: loading 0.4s linear;
        }

        &::before {
          content: '\e6d2';
        }

        svg {
          display: none;
        }
      }
    }
  }

  .el-dialog__body {
    padding: 0 !important;

    .el-input {
      height: 36px;

      .el-input__wrapper {
        padding: 1px 10px;
      }

      .el-input__inner {
        font-size: 14px;
        color: $light-gray-color;
      }
    }

    .detail-statistics {
      padding: 0 30px;
    }

    .detail-chart {
      height: 264px;

      #J_EchartArea {
        width: 1300px;
        height: 264px;
        margin: 0 auto;
      }
    }

    .detail-table {
      .table-header {
        padding: 0 30px;
        height: 40px;
        font-size: 14px;
        color: $light-gray-color;
        background: $bg-blue-color;
      }

      .table-body {
        margin: 0 30px;
        height: 320px;
        overflow-y: auto;

        .table-item {
          font-size: 16px;
          color: $black-color;
          padding: 10px 0;
          border-bottom: 1px solid $border-white-color;

          .el-image {
            background: url('~/assets/images/detail/good-bg.png') no-repeat;
            display: block;
            width: 74px;
            height: 50px;
          }
        }
      }

      .table-item-c1 {
        width: 100px;
      }
      .table-item-c2 {
        width: 360px;
      }
      .table-item-c3 {
        width: 180px;
      }
      .table-item-c4 {
        width: 160px;
      }
      .table-item-c5 {
        width: 150px;
        margin-right: 20px;
      }
    }
  }
}
</style>
